import React from 'react'
import Head from 'next/head'
import Layout from '../../components/Layout/Layout'
import format from 'date-fns/format'
import dynamic from 'next/dynamic'
import getConfig from 'next/config'
const {
  publicRuntimeConfig: { BV_SCRIPT_URL },
} = getConfig()
import { get, headers } from '../../lib/fetch'
import { getActiveExperimentVariants } from '../../lib/getActiveExperimentVariants'
import { getUserType } from '../../utils/userType'
import { AccountConsumer } from '../../components/AccountProvider'
import getPwpOffer, { filterSimplePromotion } from '../../lib/offer'
import { cookieValuetoJSON } from '../../lib/objectValidation'
import getLayoutUrl from '../../lib/getLayoutUrl'
const Error404 = dynamic(() => import('../../components/Error/404'))
import { getIosSmartBannerContent } from '../../utils/getIosSmartBannerContent'
import SprigService from '../../utils/SprigService'
import ProductDetailsLoader from '../../components/ProductDetails/ProductDetailsLoader'
import { NdsThemeProvider } from '@ntuctech/devex-tangram'
import {
  initDyCookies,
  initDyParams,
  setDySessionFromLayout,
} from '../../utils/DyUtils'
const ProductDetails = dynamic(
  () => import('../../components/ProductDetails/ProductDetails'),
  {
    loading: () => {
      return <ProductDetailsLoader />
    },
  }
)

const getProductDetails = product => {
  return (
    product.data?.page &&
    product.data.page?.layouts[0] &&
    product.data.page.layouts[0].value
  )
}

const getLevelCategories = primaryCategory => {
  const levelCategories = []
  if (!primaryCategory) {
    return {}
  }
  let currentCategory = primaryCategory
  while (currentCategory.parentCategory) {
    levelCategories.unshift(currentCategory)
    currentCategory = currentCategory.parentCategory
  }
  levelCategories.unshift(currentCategory)
  return levelCategories.reduce((acc, category, index) => {
    acc[`Product category L${index + 1}`] = category.id
    return acc
  }, {})
}

export const trackSprigPDPLanded = product => {
  if (product) {
    const details = getProductDetails(product)
    if (details?.id) {
      SprigService.track('grocery_online_product', {
        'Product id': details.id,
        ...getLevelCategories(details.primaryCategory),
      })
    }
  }
}

class Product extends React.Component {
  static async getInitialProps(appContext) {
    const dyParams = initDyParams(appContext)
    const { query, req } = appContext
    const modifiedHeaders = headers(appContext)
    if (typeof window === 'undefined') {
      modifiedHeaders['user-agent'] = appContext?.req?.headers['user-agent']
    }
    const userType = getUserType(appContext) === 'B2B' ? 'B2B' : 'DELIVERY'
    const iosSmartBannerContent = getIosSmartBannerContent(appContext)
    let pageUrl
    if (typeof window === 'undefined') {
      pageUrl = `${req.protocol}://${req.get('host')}${req.originalUrl}`
    } else {
      pageUrl = window.location.href
    }
    const checkoutAddress = cookieValuetoJSON(
      appContext,
      userType === 'B2B' ? 'b2bCheckoutAddress' : 'checkoutAddress'
    )
    const activeExperiments = getActiveExperimentVariants()
    // default call
    let api = getLayoutUrl(
      `layout/product?url=${query.slug}&includeTagDetails=true&orderType=${userType}`,
      checkoutAddress
    )
    if (activeExperiments.length) {
      api += `&experiments=${activeExperiments}`
    }
    api += dyParams
    let product = await get(api, {
      headers: modifiedHeaders,
    })
    let details = getProductDetails(product)
    if (details) {
      return {
        product,
        query,
        pageUrl,
        iosSmartBannerContent,
      }
    }

    // to support url migration
    if (/-\d+$/.test(query.slug)) {
      // to remove last `- seperated number`
      let slug = query.slug.replace(/-\d+$/, '')
      api = getLayoutUrl(
        `layout/product?url=${slug}&includeTagDetails=true&orderType=${userType}`,
        checkoutAddress
      )
      if (activeExperiments.length) {
        api += `&experiments=${activeExperiments}`
      }
      api += dyParams
      product = await get(api, {
        headers: modifiedHeaders,
      })
      details = getProductDetails(product)
      // return if details found on value of first call itself
      if (details) {
        return {
          product,
          query,
          pageUrl,
          iosSmartBannerContent,
        }
      }

      // to remove last two `- seperated numbers`
      slug = query.slug.replace(/-\d+-\d+$/, '')
      api = getLayoutUrl(
        `layout/product?url=${slug}&includeTagDetails=true&orderType=${userType}`,
        checkoutAddress
      )
      if (activeExperiments.length) {
        api += `&experiments=${activeExperiments}`
      }
      api += dyParams
      product = await get(api, {
        headers: modifiedHeaders,
      })
      return {
        product,
        query,
        pageUrl,
        iosSmartBannerContent,
      }
    }
    return {
      query,
      pageUrl,
      iosSmartBannerContent,
    }
  }

  constructor(props) {
    super(props)
    this.getSchema = this.getSchema.bind(this)
    this.loadBazarVoice = this.loadBazarVoice.bind(this)
    this.state = {
      pageUrl: props.pageUrl,
      product: this.props.product,
    }
  }

  loadBazarVoice() {
    //check if bv already exists
    if (!window.BV) {
      const tag = document.createElement('script')
      tag.async = true
      tag.src = BV_SCRIPT_URL
      tag.id = 'bvScript'
      document.body.appendChild(tag)
    }
  }

  componentDidMount() {
    setDySessionFromLayout(this.props?.product?.data?.page?.layouts)
    initDyCookies()
    if (document.readyState === 'complete') {
      this.loadBazarVoice()
      trackSprigPDPLanded(this.state.product)
    } else {
      window.addEventListener('load', () => {
        this.loadBazarVoice()
        trackSprigPDPLanded(this.state.product)
      })
    }
    if (typeof window !== 'undefined') {
      this.setState({ pageUrl: window.location.href })
    }
  }

  componentWillUnmount() {
    const elem = document.getElementById('bvScript')
    if (elem) {
      elem.parentNode.removeChild(elem)
    }
  }

  async componentDidUpdate(prevProps) {
    const dyParams = initDyParams()
    const { checkoutAddress, query } = this.props
    const userType = getUserType() === 'B2B' ? 'B2B' : 'DELIVERY'
    if (
      prevProps.checkoutAddress &&
      String(prevProps.checkoutAddress.storeId) !==
        String(checkoutAddress.storeId)
    ) {
      let api = getLayoutUrl(
        `layout/product?url=${query.slug}&includeTagDetails=true&orderType=${userType}`,
        checkoutAddress
      )
      const activeExperiments = getActiveExperimentVariants()
      if (activeExperiments.length) {
        api += `&experiments=${activeExperiments}`
      }
      api += dyParams
      let product = await get(api, {
        headers: headers(),
      })
      setDySessionFromLayout(product?.data?.page?.layouts)
      this.setState({
        product: product,
      })
    }

    if (prevProps.product !== this.props.product) {
      this.setState({ product: this.props.product })
    }

    if (window.location.href !== this.state.pageUrl) {
      this.setState({ pageUrl: window.location.href })
    }
  }

  getSchema(details) {
    let { offers, storeSpecificData, name } = details
    const statistics = details?.reviews?.statistics || {}
    const brandName = details.brand ? details.brand.name : ' '
    const productImages = []
    if (details.images && details.images.length) {
      details.images.map(productImage => productImages.push(productImage))
    }
    const processedImages = JSON.stringify(productImages)
    const barCode =
      details.barcodes && details.barcodes.length ? details.barcodes[0] : ''
    let finalPrice = storeSpecificData ? Number(storeSpecificData[0].mrp) : 0
    const validUntil = new Date(
      new Date().setFullYear(new Date().getFullYear() + 1)
    ).toISOString() // 1 year from now
    const unlimitedStock =
      storeSpecificData &&
      storeSpecificData.length > 0 &&
      storeSpecificData[0].unlimitedStock
    let isStockAvailable = false
    if (
      storeSpecificData &&
      storeSpecificData[0] &&
      storeSpecificData[0].stock > 0
    ) {
      isStockAvailable = true
    }
    if (offers && offers.length) {
      const { otherOffers } = offers ? filterSimplePromotion(offers) : {}
      if (otherOffers && otherOffers.length) {
        offers = otherOffers
      }
      const { pwpDiscount } = getPwpOffer(offers, storeSpecificData[0].mrp)
      const discountValue = pwpDiscount
        ? pwpDiscount
        : storeSpecificData[0].discount
      finalPrice = Number(storeSpecificData[0].mrp) - Number(discountValue)
    }
    return `{
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": "${name}",
      "image": ${processedImages},
      "description": "${
        details.description
          ? details.description
          : `Shop for ${name} from Singapore's trusted grocery retailer. FairPrice offers a wide range of products with prices matched online and in stores`
      }",
      "sku": "${details.id}",
      "brand": {
        "@type": "Thing",
        "name": "${brandName}"
      },
      "mpn": "${barCode}",
      "offers": {
        "@type": "Offer",
        "url": "${this.state.pageUrl}",
        "priceCurrency": "SGD",
        "price": "${finalPrice}",
        "priceValidUntil": "${validUntil}",
        "itemCondition": "https://schema.org/NewCondition",
        "availability": "${
          isStockAvailable || unlimitedStock
            ? 'https://schema.org/InStock'
            : ' https://schema.org/OutOfStock'
        }",
        "seller" : {
          "@type" : "Organization",
          "name" : "Fairprice"
        }
      },
        "review": {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "${statistics.average || null}",
            "bestRating": "${statistics.range || null}"
          },
          "author": {
            "@type": "Organization",
            "name": "Fairprice"
          }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "${statistics.average || null}",
          "reviewCount": "${statistics.total || null}"
        }
      }
    }`
  }

  render() {
    const {
      organizationData,
      categoryList,
      checkoutAddress,
      iosSmartBannerContent,
    } = this.props
    const { product } = this.state
    if (!product) {
      return (
        <Error404
          organizationData={organizationData}
          categoryList={categoryList}
          isPdp
        />
      )
    }
    const details = getProductDetails(product)
    if (!details) {
      return (
        <Error404
          organizationData={organizationData}
          categoryList={categoryList}
          isPdp
        />
      )
    }

    const { name, description, images, slug, offers } = details || {}
    let preloadProductImageUrl = null
    if (details?.images?.length) {
      preloadProductImageUrl = details?.images[0]
    }
    const offerDetail = offers && offers.length ? offers[0] : null
    const metsDescription = offerDetail
      ? `${offerDetail.description}. Grab now before ${format(
          offerDetail.validTill,
          'D MMM YYYY'
        )}!`
      : `Shop for ${name} from Singapore's trusted grocery retailer. FairPrice offers a wide range of products with prices matched online and in stores.`
    return (
      <NdsThemeProvider>
        <AccountConsumer>
          {({ isLoggedIn }) => (
            <React.Fragment>
              <Head>
                <title>{`${name} | NTUC FairPrice`}</title>
                <meta
                  name="description"
                  key="meta_description"
                  content={metsDescription}
                />
                <meta property="og:type" content="website" />
                <meta
                  property="og:title"
                  content={` ${name} | NTUC FairPrice`}
                />
                <meta property="og:description" content={description} />
                <meta property="og:image" content={images && images[0]} />
                <meta property="og:site_name" content="FairPrice" />
                <meta name="apple-itunes-app" content={iosSmartBannerContent} />
                <link rel="canonical" href={`/product/${slug}`} />
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{ __html: this.getSchema(details) }}
                />
                {preloadProductImageUrl && (
                  <link
                    rel="preload"
                    href={`${preloadProductImageUrl}?w=400&q=70`}
                    as="image"
                  />
                )}
              </Head>

              <Layout
                organizationData={organizationData}
                noWrapper="fromMobile"
                categoryList={categoryList}
              >
                <div key={details.id}>
                  <ProductDetails
                    data={{ ...this.props, product }}
                    isLoggedIn={isLoggedIn}
                    checkoutAddress={checkoutAddress}
                    preloadProductImageUrl={preloadProductImageUrl}
                  />
                </div>
              </Layout>
            </React.Fragment>
          )}
        </AccountConsumer>
      </NdsThemeProvider>
    )
  }
}

Product.defaultProps = {}

export default Product
